import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSuppliers, getPriceStatus } from '@/hooks/useSuppliers';
import { formatPriceCt } from '@/utils/formatters';
import { Plus, Package, Search, Save, X, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';

const SuppliersList = () => {
  const navigate = useNavigate();
  // Bewusst nur loadError: ein fehlgeschlagenes Speichern darf die Liste weder
  // leeren noch als "nicht geladen" darstellen — es wird im Formular gezeigt.
  const { suppliers, prices, addSupplier, marginSettings, loading, loadError, reload } = useSuppliers();
  const [showNewModal, setShowNewModal] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [newForm, setNewForm] = useState({ name: '', contact_name: '', contact_email: '', contact_phone: '', notes: '' });

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim()) return;
    setSaving(true);
    setFormError('');
    try {
      const s = await addSupplier(newForm);
      setShowNewModal(false);
      setNewForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', notes: '' });
      navigate(`/app/versorger/${s.id}`);
    } catch (err) {
      setFormError(err.message || 'Versorger konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  // Nur heute gueltige Preise zaehlen — abgelaufene oder erst spaeter startende
  // Tarife duerfen nicht als "bester Einkaufspreis" erscheinen.
  const activePrices = (id) => prices.filter(
    p => String(p.supplier_id) === String(id) && getPriceStatus(p) === 'aktiv'
  );
  const getPriceCount = (id) => activePrices(id).length;
  const getBestPrice = (id) => {
    const sp = activePrices(id).map(p => p.price_ct_kwh).filter(v => v != null);
    return sp.length ? Math.min(...sp) : null;
  };

  const filtered = search
    ? suppliers.filter(s => (s.name || '').toLowerCase().includes(search.toLowerCase()))
    : suppliers;
  const inputCls = "w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="pg-title">Versorger</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {loading
              ? 'Lade Versorger …'
              : loadError
                ? 'Daten konnten nicht geladen werden'
                : <>{suppliers.length} Versorger &middot; Standard-Marge: {marginSettings.default_margin_ct_kwh} ct/kWh</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reload} disabled={loading} className="flex items-center gap-2 border border-border px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />Aktualisieren
          </button>
          <button onClick={() => { setFormError(''); setShowNewModal(true); }} className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm">
            <Plus size={16} />Neuer Versorger
          </button>
        </div>
      </div>

      {loadError && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={18} className="text-destructive mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-destructive">Versorgerdaten konnten nicht geladen werden</p>
            <p className="text-muted-foreground mt-0.5">{loadError}</p>
            <button onClick={reload} className="mt-2 text-primary font-medium hover:underline">Erneut versuchen</button>
          </div>
        </div>
      )}

      <div className="hk-card">
        <div className="hk-card-header">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Versorger suchen..." className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Versorger</th>
                <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Ansprechpartner</th>
                <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Aktive Tarife</th>
                <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Bester EK</th>
                <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">VK (mit Marge)</th>
                <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-muted-foreground">Lade Versorger …</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Package className="mx-auto text-muted-foreground/40 mb-3" size={32} />
                    <p className="text-sm text-muted-foreground">
                      {loadError
                        ? 'Keine Daten verfuegbar'
                        : suppliers.length === 0
                          ? 'Noch keine Versorger angelegt'
                          : 'Kein Versorger passt zur Suche'}
                    </p>
                  </td>
                </tr>
              )}
              {!loading && filtered.map(sup => {
                const best = getBestPrice(sup.id);
                return (
                  <tr key={sup.id} onClick={() => navigate(`/app/versorger/${sup.id}`)} className="hover:bg-muted/30 cursor-pointer group transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center"><Package className="text-primary" size={18} /></div>
                        <div>
                          <p className="font-semibold text-foreground">{sup.name}</p>
                          <p className="text-xs text-muted-foreground">{sup.contact_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">{sup.contact_name || '–'}</td>
                    <td className="px-6 py-4"><span className="text-sm font-semibold">{getPriceCount(sup.id)}</span></td>
                    <td className="px-6 py-4">{best != null ? <span className="text-sm font-bold text-success">{formatPriceCt(best)} ct</span> : <span className="text-muted-foreground text-xs">–</span>}</td>
                    <td className="px-6 py-4">{best != null ? <span className="text-sm font-bold text-primary">{formatPriceCt(best + marginSettings.default_margin_ct_kwh)} ct</span> : <span className="text-muted-foreground text-xs">–</span>}</td>
                    <td className="px-6 py-4"><span className={`hk-badge ${sup.is_active ? 'success' : 'secondary'}`}>{sup.is_active ? 'Aktiv' : 'Inaktiv'}</span></td>
                    <td className="px-4 py-4 text-right"><ChevronRight className="text-muted-foreground group-hover:text-primary transition-colors" size={18} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neuer Versorger</DialogTitle></DialogHeader>
          <form onSubmit={handleAddSupplier} className="space-y-4">
            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">{formError}</p>
            )}
            <div><label className="text-sm font-semibold mb-1.5 block">Versorgername *</label><input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="z.B. E.ON Energie" required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-semibold mb-1.5 block">Ansprechpartner</label><input value={newForm.contact_name} onChange={e => setNewForm(f => ({ ...f, contact_name: e.target.value }))} className={inputCls} /></div>
              <div><label className="text-sm font-semibold mb-1.5 block">E-Mail</label><input value={newForm.contact_email} onChange={e => setNewForm(f => ({ ...f, contact_email: e.target.value }))} className={inputCls} /></div>
            </div>
            <div><label className="text-sm font-semibold mb-1.5 block">Telefon</label><input value={newForm.contact_phone} onChange={e => setNewForm(f => ({ ...f, contact_phone: e.target.value }))} className={inputCls} /></div>
            <div className="flex justify-end gap-3 pt-3 border-t border-border">
              <button type="button" onClick={() => setShowNewModal(false)} className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"><X size={16} />Abbrechen</button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"><Save size={16} />{saving ? 'Speichert …' : 'Anlegen'}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersList;
